/**
 * Karplus–Strong plucked strings (oud / qanun / low oud), rendered offline into AudioBuffers once
 * per (kind, pitch) and cached. Rendering is ~0.3 ms per note; the cache is warmed a note per
 * frame so a theme switch never hitches.
 */
import { clamp, mtof, semis, toBuffer, type Rng } from '../util';
import { link, type NoteOpts, type Synth } from './synth';

export type PluckKind = 'oud' | 'qanun' | 'bass';

interface KsSpec {
  /** Rendered length at 196 Hz (s); shorter for higher notes. */
  readonly dur: number;
  /** Excitation brightness 0..1 (one-pole low-pass on the noise burst). */
  readonly bright: number;
  /** T60 of the fundamental at 196 Hz (s). */
  readonly t60: number;
  /** Pluck position (fraction of the string) — comb-filters the excitation. */
  readonly pick: number;
  /** Loop-filter weight on the previous sample (0.5 = classic averaging, lower = brighter). */
  readonly stretch: number;
}

const SPECS: Record<PluckKind, KsSpec> = {
  oud: { dur: 1.45, bright: 0.42, t60: 2.4, pick: 0.17, stretch: 0.5 },
  qanun: { dur: 1.55, bright: 0.74, t60: 3.0, pick: 0.11, stretch: 0.4 },
  bass: { dur: 1.6, bright: 0.26, t60: 2.6, pick: 0.21, stretch: 0.5 },
};

/** ~200 KB per note at 48 kHz → a few MB at most. */
const MAX_CACHED = 40;

function renderKs(sr: number, f: number, spec: KsSpec, rng: Rng): Float32Array<ArrayBuffer> {
  const scale = clamp(Math.pow(196 / f, 0.3), 0.45, 1.25);
  const n = Math.max(256, Math.floor(sr * spec.dur * scale));
  const out = new Float32Array(n);
  const S = spec.stretch;
  const period = sr / f;
  // Total loop delay = N (line) + S (loop filter) + frac (allpass); keep frac in [0.1, 1.1).
  const N = Math.max(2, Math.floor(period - S - 0.1));
  const frac = period - S - N;
  const C = (1 - frac) / (1 + frac);
  const t60 = spec.t60 * clamp(Math.pow(196 / f, 0.35), 0.35, 1.6);
  const g = Math.pow(0.001, 1 / (f * t60));

  // Excitation: low-passed noise burst, comb-filtered at the pluck position, DC-free.
  const line = new Float32Array(N);
  const a = 0.08 + 0.85 * spec.bright;
  let lp = 0;
  for (let i = 0; i < N; i++) {
    lp += a * (rng.bi() - lp);
    line[i] = lp;
  }
  const P = Math.max(1, Math.round(spec.pick * N));
  for (let i = N - 1; i >= P; i--) line[i] = (line[i] as number) - (line[i - P] as number);
  let mean = 0;
  for (let i = 0; i < N; i++) mean += line[i] as number;
  mean /= N;
  let pk = 0;
  for (let i = 0; i < N; i++) {
    const v = (line[i] as number) - mean;
    line[i] = v;
    pk = Math.max(pk, Math.abs(v));
  }
  if (pk > 0) for (let i = 0; i < N; i++) line[i] = (line[i] as number) / pk;

  let idx = 0;
  let prev = 0;
  let apX = 0;
  let apY = 0;
  let peak = 0;
  for (let i = 0; i < n; i++) {
    const y = line[idx] as number;
    out[i] = y;
    const ay = Math.abs(y);
    if (ay > peak) peak = ay;
    const filt = g * ((1 - S) * y + S * prev);
    prev = y;
    const ap = C * filt + apX - C * apY;
    apX = filt;
    apY = ap;
    line[idx] = ap;
    idx++;
    if (idx >= N) idx = 0;
  }

  // Normalise, soften the very first samples (plectrum, not a click) and fade the tail to 0.
  const k = peak > 0 ? 0.9 / peak : 0;
  const fadeIn = Math.min(n, Math.floor(sr * 0.0015));
  const fadeOut = Math.min(n, Math.floor(sr * 0.04));
  for (let i = 0; i < n; i++) {
    let w = k;
    if (i < fadeIn) w *= i / fadeIn;
    const fromEnd = n - 1 - i;
    if (fromEnd < fadeOut) w *= fromEnd / fadeOut;
    out[i] = (out[i] as number) * w;
  }
  return out;
}

export class PluckCache {
  private readonly cache = new Map<string, AudioBuffer>();
  private readonly queue: [PluckKind, number][] = [];

  constructor(
    private readonly ctx: BaseAudioContext,
    private readonly rng: Rng,
  ) {}

  get(kind: PluckKind, midi: number): AudioBuffer {
    const m = Math.round(midi);
    const key = `${kind}:${m}`;
    const hit = this.cache.get(key);
    if (hit) {
      // LRU touch
      this.cache.delete(key);
      this.cache.set(key, hit);
      return hit;
    }
    const data = renderKs(this.ctx.sampleRate, mtof(m), SPECS[kind], this.rng);
    const buf = toBuffer(this.ctx, [data]);
    this.cache.set(key, buf);
    if (this.cache.size > MAX_CACHED) {
      const first = this.cache.keys().next();
      if (!first.done) this.cache.delete(first.value);
    }
    return buf;
  }

  /** Queue notes to pre-render incrementally (see pump). */
  warm(kind: PluckKind, midis: readonly number[]): void {
    for (const m of midis) {
      const r = Math.round(m);
      if (!this.cache.has(`${kind}:${r}`)) this.queue.push([kind, r]);
    }
  }

  /** Render up to `count` queued notes (call once per frame). */
  pump(count = 1): void {
    for (let i = 0; i < count && this.queue.length > 0; i++) {
      const job = this.queue.shift();
      if (job) this.get(job[0], job[1]);
    }
  }

  clear(): void {
    this.cache.clear();
    this.queue.length = 0;
  }
}

interface PluckVoicing {
  readonly kind: PluckKind;
  readonly bodyHz: number;
  readonly bodyDb: number;
  readonly lp: number;
  readonly level: number;
  readonly send: number;
}

const OUD: PluckVoicing = { kind: 'oud', bodyHz: 280, bodyDb: 5, lp: 3600, level: 0.42, send: 0.25 };
const QANUN: PluckVoicing = { kind: 'qanun', bodyHz: 520, bodyDb: 3, lp: 7500, level: 0.62, send: 0.3 };
const BASS: PluckVoicing = { kind: 'bass', bodyHz: 140, bodyDb: 4, lp: 1300, level: 0.5, send: 0.12 };

export interface PluckOpts extends NoteOpts {
  /** Slide into the note from this many semitones below (oud portamento ornament). */
  readonly slide?: number;
}

function playPluck(s: Synth, pv: PluckVoicing, t: number, midi: number, vel: number, o: PluckOpts): void {
  const buf = s.kit.plucks.get(pv.kind, midi);
  const v = s.pool.voice(t, s.route, { pan: o.pan, send: o.send ?? pv.send });
  const src = v.src(buf);
  const rate = src.playbackRate;
  let minRate = 1;
  if (o.slide) {
    const r0 = semis(-Math.abs(o.slide));
    rate.setValueAtTime(r0, t);
    rate.exponentialRampToValueAtTime(1, t + 0.07);
    minRate = Math.min(minRate, r0);
  }
  if (o.bend) {
    const r1 = semis(o.bend);
    rate.setValueAtTime(1, t + 0.08);
    rate.exponentialRampToValueAtTime(r1, t + 0.5);
    minRate = Math.min(minRate, r1);
  }
  const body = v.filter('peaking', pv.bodyHz, 1.1);
  body.gain.value = pv.bodyDb;
  const lp = v.filter('lowpass', o.muted ? pv.lp * 0.4 : pv.lp, 0.6);
  const g = v.gain(pv.level * vel);
  link(src, body, lp, g, v.out);
  const len = (buf.duration / minRate) * (o.decay !== undefined ? Math.min(1, o.decay) : 1);
  if (len < buf.duration / minRate - 0.01) {
    // Shortened (damped) note: fade it out rather than cutting.
    g.gain.setValueAtTime(pv.level * vel, t + len * 0.6);
    g.gain.linearRampToValueAtTime(0, t + len);
  }
  v.play(src, t, t + len + 0.02);
}

export function oud(s: Synth, t: number, midi: number, vel: number, o: PluckOpts = {}): void {
  playPluck(s, OUD, t, midi, vel, o);
}

export function qanun(s: Synth, t: number, midi: number, vel: number, o: PluckOpts = {}): void {
  playPluck(s, QANUN, t, midi, vel, o);
}

export function oudBass(s: Synth, t: number, midi: number, vel: number, o: PluckOpts = {}): void {
  playPluck(s, BASS, t, midi, vel, o);
}
